/** ZITADEL OIDC for studio-ui — ported from builder-ui (same client/scopes).
 *
 *  Auth is OPTIONAL in dev: analytics + builder read endpoints resolve to the
 *  demo org without a token (JWT_ENFORCE=false), so the app renders signed-out.
 *  Signing in (ZITADEL) unlocks the auth-gated surfaces (plan, team). */

import { useEffect } from "react";
import type { ReactNode } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { setAuthToken } from "@/api/client";

const authority = import.meta.env.VITE_OIDC_AUTHORITY as string | undefined;
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined;
const redirectUri = import.meta.env.VITE_OIDC_REDIRECT_URI as string | undefined;
const postLogoutUri = import.meta.env.VITE_OIDC_POST_LOGOUT_URI as string | undefined;
const projectId = import.meta.env.VITE_OIDC_PROJECT_ID as string | undefined;

/** True when OIDC is configured (authority + client id present). Route guards
 *  only enforce auth when this is true — so a checkout without OIDC env still
 *  runs on demo data instead of bouncing to a sign-in that can't complete. */
export const authConfigured = !!(authority && clientId);

const scope =
  "openid profile email offline_access " +
  "urn:zitadel:iam:org:project:id:" + projectId + ":aud " +
  "urn:zitadel:iam:org:projects:roles " +
  "urn:zitadel:iam:user:resourceowner";

const oidcConfig: AuthProviderProps = {
  authority: authority ?? "",
  client_id: clientId ?? "",
  redirect_uri: redirectUri ?? "",
  post_logout_redirect_uri: postLogoutUri ?? "",
  response_type: "code",
  scope,
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, "/today");
  },
};

/** Mirror the OIDC token into the api client so requests attach the Bearer. */
export function TokenSync() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  useEffect(() => {
    setAuthToken(token);
  }, [token]);
  return null;
}

export function AppAuthProvider({ children }: { children: ReactNode }) {
  // If OIDC isn't configured, render children without the provider so the app
  // still works on demo data.
  if (!authority || !clientId) return <>{children}</>;
  return (
    <AuthProvider {...oidcConfig}>
      <TokenSync />
      {children}
    </AuthProvider>
  );
}

/** Current user from OIDC claims (no GET /me). Safe when signed out / no provider. */
export function useMe() {
  let auth: ReturnType<typeof useAuth> | null = null;
  try {
    auth = useAuth();
  } catch {
    auth = null;
  }
  const p = auth?.user?.profile;
  return {
    isAuthenticated: !!auth?.isAuthenticated,
    isLoading: !!auth?.isLoading,
    user: p
      ? {
          id: (p.sub as string) ?? "",
          email: (p.email as string) ?? "",
          name: (p.name as string) ?? (p.email as string) ?? "You",
        }
      : null,
    // Optional email is forwarded to ZITADEL as a login_hint so the hosted
    // login can prefill it; credentials are always collected on ZITADEL's page.
    signIn: (email?: string) => auth?.signinRedirect(email ? { login_hint: email } : undefined),
    signOut: () => auth?.signoutRedirect(),
  };
}
